import React, { FC, useMemo } from "react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { FoldableContainer } from "@/components/pages/chats/component/booking-info-components/FoldableContainer";
import { billingTransactionKeys } from "@/hooks/useBillingTransactions";
import { billingTransactionService } from "@/services/billing-transactions-service";
import {
  BillingTransaction,
  BillingTransactionStatus,
  BillingTransactionType,
} from "@/types/billing-transactions";
import { Reservation } from "@/types/reservation";
import { useTranslation } from "react-i18next";
import { formatMinorCurrency } from "@/utils/billingBalance";
import { getStringMeta } from "@/components/pages/reservations/utils/getStringMeta";
import { cn } from "@/lib/utils";

interface PaymentBookingInfoProps {
  booking: Reservation;
}

const MAX_VISIBLE_TRANSACTIONS = 6;

export const PaymentBookingInfo: FC<PaymentBookingInfoProps> = ({
  booking,
}) => {
  const { t } = useTranslation();
  const guestId = booking.guestId;

  const { data, isLoading } = useQuery({
    queryKey: billingTransactionKeys.list({ guestId }),
    queryFn: () =>
      billingTransactionService.getBillingTransactions(
        { guestId },
        1,
        MAX_VISIBLE_TRANSACTIONS,
      ),
    enabled: Boolean(guestId),
    staleTime: 5 * 60 * 1000,
  });

  const transactions = useMemo(
    () =>
      [...(data?.transactions ?? [])].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [data?.transactions],
  );

  const extraNode = transactions.length ? (
    <Badge variant="outline">{transactions.length}</Badge>
  ) : undefined;

  if (!guestId || (!isLoading && !transactions.length)) {
    return null;
  }

  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <div className="mt-3 text-sm text-slate-500">
        {t("billing.loadingBillingStatus")}
      </div>
    );
  } else {
    content = (
      <div className="relative mt-4 flex flex-col gap-0">
        <div className="absolute bottom-0 left-[7px] top-0 w-px bg-slate-200" />
        {transactions.map((transaction) => (
          <TransactionLogItem key={transaction.id} transaction={transaction} />
        ))}
      </div>
    );
  }

  return (
    <FoldableContainer
      titleNode={t("billing.transactionLogs")}
      extraNode={extraNode}
    >
      {content}
    </FoldableContainer>
  );
};

const TransactionLogItem = ({
  transaction,
}: {
  transaction: BillingTransaction;
}) => {
  const { t } = useTranslation();
  const description =
    transaction.billingItems
      ?.map((item) => item.description)
      .filter((value): value is string => Boolean(value))
      .join(", ") || "";
  const createdByName = getStringMeta(transaction.metadata, "createdByName");
  const statusLabel = getStatusLabel(transaction.status, t);
  const title = getTransactionTitle(transaction.type, t);

  return (
    <div className="relative pl-7 pb-4 last:pb-0">
      <span
        className={cn(
          "absolute left-0 top-1.5 size-4 rounded-full border-2 border-white",
          getStatusDotClassName(transaction.status),
        )}
      />

      <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-slate-900">
                {title}
              </span>
              <Badge variant="outline" className="text-[10px] font-medium">
                {statusLabel}
              </Badge>
            </div>

            <div className="mt-1 text-xs text-slate-500">
              {format(new Date(transaction.createdAt), "dd.MM.yyyy, HH:mm")}
            </div>
          </div>

          <div className="shrink-0 text-sm font-medium text-slate-900">
            {formatMinorCurrency(transaction.amount, transaction.currency)}
          </div>
        </div>

        {(description || createdByName) && (
          <div className="mt-2 flex flex-col gap-1 text-xs text-slate-600">
            {description ? <div>{description}</div> : null}
            {createdByName ? (
              <div>
                {t("billing.createdBy")}: {createdByName}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

function getTransactionTitle(
  type: BillingTransactionType,
  t: (key: string, fallback?: string) => string,
) {
  if (type === BillingTransactionType.REFUND) {
    return t("billing.refund", "Refund");
  }

  if (type === BillingTransactionType.PARTIAL_REFUND) {
    return t("billing.partialRefund", "Partial refund");
  }

  return t("billing.payment", "Payment");
}

function getStatusLabel(
  status: BillingTransactionStatus,
  t: (key: string, fallback?: string) => string,
) {
  switch (status) {
    case BillingTransactionStatus.SUCCEEDED:
      return t("billing.succeeded");
    case BillingTransactionStatus.FAILED:
      return t("billing.failed");
    case BillingTransactionStatus.REFUNDED:
      return t("billing.refunded");
    case BillingTransactionStatus.PARTIALLY_REFUNDED:
      return t("billing.partiallyRefunded");
    case BillingTransactionStatus.PROCESSING:
      return t("billing.processing");
    case BillingTransactionStatus.PENDING:
      return t("billing.pending");
    case BillingTransactionStatus.CANCELED:
      return t("billing.canceled");
    case BillingTransactionStatus.REQUIRES_ACTION:
      return t("billing.requiresAction");
    case BillingTransactionStatus.REQUIRES_PAYMENT_METHOD:
      return t("billing.requiresPaymentMethod");
    default:
      return status;
  }
}

function getStatusDotClassName(status: BillingTransactionStatus) {
  switch (status) {
    case BillingTransactionStatus.SUCCEEDED:
      return "bg-emerald-500";
    case BillingTransactionStatus.REFUNDED:
    case BillingTransactionStatus.PARTIALLY_REFUNDED:
      return "bg-amber-500";
    case BillingTransactionStatus.FAILED:
    case BillingTransactionStatus.CANCELED:
      return "bg-rose-500";
    case BillingTransactionStatus.PROCESSING:
    case BillingTransactionStatus.PENDING:
    case BillingTransactionStatus.REQUIRES_ACTION:
    case BillingTransactionStatus.REQUIRES_PAYMENT_METHOD:
      return "bg-sky-500";
    default:
      return "bg-slate-400";
  }
}
